import backTriangleBlue from "@/assets/back-triangle-blue.svg";
import continueTriangleOrange from "@/assets/continue-triangle-orange.svg";
import Button from "@/common/elements/Button";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import { INPUT_VARIANT } from "@/common/elements/InputElements/const";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import FormActionsFooter from "../FormActionsFooter";
import AdministrativeInformation from "./AdministrativeInformation";
import BailInformation from "./BailInformation";
import ChargeInformation from "./ChargeInformation";
import ClearanceInformation from "./ClearanceInformation";
import CourtInformation from "./CourtInformation";
import DeadlineInformation from "./DeadlineInformation";
import OffenseInformation from "./OffenseInformation";
import OriginalChargeInformation from "./OriginalChargeInformation";
import SentencingInformation from "./SentencingInformation";
import { AddChargesContainer, ChargesSectionContainer } from "./styles";
import { Flex, Form } from "antd";
import dayjs from "dayjs";
import useDialog from "@/hooks/useDialog";
import {
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { useSelector } from "react-redux";
import { useClientParam, useClientParamBool } from "@/hooks/useClientParam";

export const MODE = { NEW: "NEW", EDIT: "EDIT", VIEW: "VIEW" };

const formSteps = [
  { value: "charge-information", label: "Charge Information" },
  { value: "deadline-information", label: "Deadline Information" },
  { value: "offense-information", label: "Offense Information" },
  { value: "court-information", label: "Court Information" },
  {
    value: "additional-court-information",
    label: "Additional Court Information",
  },
  { value: "administrative-information", label: "Administrative Information" },
  {
    value: "original-charge-information",
    label: "Original Charge Information",
  },
  { value: "bail-information", label: "Bail Information" },
  { value: "sentencing-information", label: "Sentencing Information" },
  { value: "clearance-information", label: " Clearance Information" },
];

const AddCharges = forwardRef(
  (
    {
      formData,
      isEdit,
      chargeFormRef,
      bookingBails,
      isBooking = false,
      courtListsData,
      updateCourtListForm,
      chargesData,
      startingPoint,
    },
    ref,
  ) => {
    const [form] = Form.useForm();
    const [loginUser, setLoginUser] = useState(null);
    const { showDialog } = useDialog();
    const [controllingChargeValidation, setControllingChargeValidation] =
      useState(true);
    const [holidays, setHolidays] = useState([]);

    // -----------------------------------------------------------------------
    // ChargesExtra — dynamic per-client fields (PCI shows these under
    // "Agency-Specific Information", inside ChargesInterface.asp, right
    // after Original Charge Information). That heading already exists in
    // OriginalChargeInformation.jsx with 6 hardcoded fields matching
    // ChargesExtra's real columns — but none of those SecureFormItems had a
    // `name`, so they were never actually captured or saved. This fetches
    // the real field defs + (when editing) the saved row, and
    // OriginalChargeInformation renders them dynamically instead.
    // -----------------------------------------------------------------------
    const [chargesExtraFields, setChargesExtraFields] = useState([]);
    // True once chargesExtraTypeMapRef is populated — gates the fetch below
    // so a date/datetime extra column's value is never handed to the form
    // as a plain string (see AdditionalPersonalInformation.jsx for why that
    // matters — antd's DatePicker crashes calling .isValid() on one).
    const [chargesExtraFieldsReady, setChargesExtraFieldsReady] = useState(false);
    // columnName -> htmlType ("date" / "datetime" / ...) for every
    // ChargesExtra field.
    const chargesExtraTypeMapRef = useRef({});
    const [chargesExtraFormValues, setChargesExtraFormValues] = useState(null);
    // The extra table's own PK (0 when this charge has no saved row yet) —
    // exposed via chargeFormRef so EditChargesModal's save can tell CREATE
    // from UPDATE.
    const chargesExtraIDRef = useRef(0);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/ChargesExtra`)
        .then((response) => {
          const tableData = response?.data?.filter((d) =>
            d?.columnName?.endsWith("_U"),
          );
          const converted = convertApiFields(tableData, "ChargesExtra");

          const typeMap = {};
          converted.forEach((c) => {
            typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
          });
          chargesExtraTypeMapRef.current = typeMap;

          setChargesExtraFields(converted);
          setChargesExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error(
            "Request failed:",
            error.response ? error.response.data : error.message,
          );
        });
    }, []);

    // Fetches the saved ChargesExtra row when editing an existing charge —
    // GET /api/extra/ChargesExtra/chargeID/{id}, same generic endpoint
    // BioExtra/ArrestReportsExtra/InmatePropertyExtra use.
    useEffect(() => {
      if (!chargesExtraFieldsReady) return;

      const chargeID = formData?.chargeID;
      if (!chargeID) {
        chargesExtraIDRef.current = 0;
        setChargesExtraFormValues(null);
        return;
      }

      fetchExtraTableRecord("ChargesExtra", "chargeID", chargeID)
        .then((result) => {
          if (!result) {
            chargesExtraIDRef.current = 0;
            setChargesExtraFormValues(null);
            return;
          }
          chargesExtraIDRef.current = result.raw?.ChargesExtraID ?? 0;

          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = chargesExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          setChargesExtraFormValues(withDates);
        })
        .catch((error) => {
          console.error(
            "Request failed:",
            error.response ? error.response.data : error.message,
          );
        });
    }, [formData?.chargeID, chargesExtraFieldsReady]);

    // Pushed into the form separately from the main `formData` sync effect
    // below so this fetch resolving doesn't re-trigger that effect's other
    // (unrelated) side effects.
    useEffect(() => {
      if (isNonEmptyVariable(chargesExtraFormValues)) {
        form.setFieldsValue(chargesExtraFormValues);
      }
    }, [chargesExtraFormValues]);

    const fullSearch = useSelector((state) => state.fullSearch);
    const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);

    // Bookings\UseEHM — gates the EHM section in Sentencing Information,
    // same as ClientGetClientParam("Bookings", "UseEHM", "False") in the
    // legacy ASP app. Bookings\NumberEHMDays is the default EHM Days value
    // auto-filled when EHM? is first checked (mirrors EHMChanged()).
    const useEHM = useClientParamBool("Bookings", "UseEHM", false);
    const numberEHMDaysDefault = useClientParam(
      "Bookings",
      "NumberEHMDays",
      "45",
    );
    // Bookings\DefaultReleaseTime — the release time used for the
    // Scheduled Release Date/Time whenever sentenceLength > 5 (the <=5
    // bracket instead uses the sentence's own start time). Stored as
    // "HH:mm"; falls back to 00:01 like the legacy default.
    const defaultReleaseTime = useClientParam(
      "Bookings",
      "DefaultReleaseTime",
      "00:01",
    );
    const [rawReleaseHour, rawReleaseMinute] = defaultReleaseTime
      .split(":")
      .map((v) => parseInt(v));
    const defaultReleaseHour = isNaN(rawReleaseHour) ? 0 : rawReleaseHour;
    const defaultReleaseMinute = isNaN(rawReleaseMinute) ? 1 : rawReleaseMinute;
    // Charges\UseAdditionalGoodTimeDays — gates the Additional Good Time
    // Days field, same as ClientGetClientParam("Charges",
    // "UseAdditionalGoodTimeDays", "False"). Only applies once
    // sentenceLength > 9 (legacy hides/zeroes it for shorter sentences).
    const useAdditionalGoodTimeDays = useClientParamBool(
      "Charges",
      "UseAdditionalGoodTimeDays",
      false,
    );
    // Charges\GoodTimeDays — when set to "Base10Rule", good time days are
    // baseNumberOfDays / 10 instead of the default baseNumberOfDays / 3.
    const goodTimeDaysRule = useClientParam("Charges", "GoodTimeDays", "");
    // Charges\Sentence_24hrDay_Limit — when chaining a consecutive charge's
    // Sentence Start Date off another charge's Scheduled Release Date, skip
    // a day if that other charge's own sentence was long enough to have
    // gotten the 1-minute credit (default threshold: 5 days).
    const sentence24hrDayLimit =
      parseInt(useClientParam("Charges", "Sentence_24hrDay_Limit", "5")) || 5;

    useImperativeHandle(chargeFormRef, () => ({
      form,
      // Consumed by EditChargesModal's save to split ChargesExtra fields out
      // of the main charge payload and into their own /api/extra/save call.
      chargesExtraFieldKeys: chargesExtraFields.map((f) => f.columnName),
      chargesExtraTypeMap: chargesExtraTypeMapRef.current,
      chargesExtraID: chargesExtraIDRef.current,
    }));

    // -----------------------------------------------------------------------
    // Form.useWatch — conditional visibility & derived field logic
    // -----------------------------------------------------------------------
    const controllingCharge = Form.useWatch("controllingCharge", form);
    const requiresBond = Form.useWatch("requiresBond", form);
    const sentenced = Form.useWatch("sentenced", form);
    const clearedToGo = Form.useWatch("clearedToGo", form);
    const originalCustodyReasonID = Form.useWatch(
      "originalCustodyReasonID",
      form,
    );
    const chargeDescriptionID = Form.useWatch("chargeDescriptionID", form);
    const deadlineStartDateTime = Form.useWatch("deadlineStartDateTime", form);
    const offenderDispositionID = Form.useWatch("offenderDispositionID", form);
    const sentenceConsecutive = Form.useWatch("sentenceConsecutive", form);
    const sentenceConsecutiveTo = Form.useWatch("sentenceConsecutiveTo", form);
    const releaseDateOverridden = Form.useWatch("releaseDateOverridden", form);
    const sentenceLength = Form.useWatch("sentenceLength", form);
    const stayedOrSuspendedDays = Form.useWatch("stayedOrSuspendedDays", form);
    const treatmentDays = Form.useWatch("treatmentDays", form);
    const creditDaysServed = Form.useWatch("creditDaysServed", form);
    const goodTimeDaysLost = Form.useWatch("goodTimeDaysLost", form);
    const additionalGoodTimeDays = Form.useWatch(
      "additionalGoodTimeDays",
      form,
    );
    const sentenceStartDateTime = Form.useWatch("sentenceStartDateTime", form);
    const ehm = Form.useWatch("ehm", form);
    const ehmDays = Form.useWatch("ehmDays", form);
    const originalChargeDescriptionID = Form.useWatch(
      "originalChargeDescriptionID",
      form,
    );
    const totalDaysForThisCharge = Form.useWatch(
      "totalDaysForThisCharge",
      form,
    );

    // -----------------------------------------------------------------------
    // Fetch holidays on mount
    // -----------------------------------------------------------------------
    useEffect(() => {
      const getAllHolidays = async () => {
        try {
          const { data } = await api.get(API_ENDPOINTS.config.holidays.getAll);
          if (data?.length) setHolidays(data);
        } catch (error) {
          console.log(error);
        }
      };
      getAllHolidays();
    }, []);

    // -----------------------------------------------------------------------
    // Init — user, controlling charge validation, default values
    // -----------------------------------------------------------------------
    useEffect(() => {
      setLoginUser(JSON.parse(localStorage.getItem("userDetails")));

      if (isEdit && isNonEmptyVariable(chargesData) && chargesData?.length) {
        const hasMultipleControlling =
          chargesData.filter(
            (c) => c?.controllingCharge || c?.controllingCharge === "True",
          ).length > 1;
        setControllingChargeValidation(!hasMultipleControlling);
      }

      setTimeout(() => {
        if (chargesData?.length === 0 && !fullSearch) {
          form.setFieldsValue({ controllingCharge: true });
        }
      }, 100);
    }, []);

    // -----------------------------------------------------------------------
    // Sync formData → form
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (!isEdit) {
        form.setFieldsValue({
          sentenceDate: null,
          sentenceStartDateTime: null,
          sentenceEndDate: null,
          scheduledReleaseDateTime: null,
        });
      }
      if (!fullSearch) {
        setTimeout(() => form.setFieldValue("chargeCount", 1), 100);
      }
      if (isNonEmptyVariable(formData)) {
        form.setFieldsValue(formData);
      }
    }, [formData]);

    // -----------------------------------------------------------------------
    // Controlling charge validation — only one controlling charge is allowed
    // per booking. "Other" charges excludes the record currently being
    // edited (matched by uuid) so re-saving the existing controlling charge
    // doesn't trip its own restriction.
    // -----------------------------------------------------------------------
    useEffect(() => {
      const otherCharges = isNonEmptyVariable(chargesData)
        ? chargesData.filter(
            (c) =>
              !isNonEmptyVariable(formData?.uuid) || c?.uuid !== formData?.uuid,
          )
        : [];
      const hasOtherControlling = otherCharges.some(
        (c) => c?.controllingCharge || c?.controllingCharge === "True",
      );

      if (!fullSearch && controllingCharge && hasOtherControlling) {
        showDialog("warning", {
          title: "Controlling Charge",
          content:
            "Only one charge can be the Controlling Charge for this booking. Please uncheck the existing controlling charge before marking this one.",
          confirmButtonText: "Ok",
        });
        form.setFieldValue("controllingCharge", false);
        return;
      }

      setControllingChargeValidation(!hasOtherControlling);
    }, [controllingCharge]);

    // -----------------------------------------------------------------------
    // Charge description → auto-fill charge level + UCR code
    // -----------------------------------------------------------------------
    const applyChargeLevel = (chargeLevel, levelField, ucrField) => {
      const levelMap = {
        FE: 51500001,
        GM: 51500003,
        MS: 51500006,
        PM: 51500004,
      };
      if (levelMap[chargeLevel])
        form.setFieldValue(levelField, levelMap[chargeLevel]);
    };

    useEffect(() => {
      if (isNonEmptyVariable(chargeDescriptionID)) {
        api
          .get(
            `${API_ENDPOINTS.vwCharges_refListItemGlobalID}/${chargeDescriptionID}`,
          )
          .then(({ data }) => {
            if (data?.length) {
              applyChargeLevel(data[0].chargeLevel, "chargeLevelID");
              const ucr = data[0].refListItemAttribs?.find(
                (r) => r.attribName === "UOC_Code",
              )?.attribValue;
              if (isNonEmptyVariable(ucr)) form.setFieldValue("ucrCode", ucr);
            }
          });
      } else {
        form.setFieldValue("chargeLevelID", "");
        form.setFieldValue("ucrCode", "");
      }
    }, [chargeDescriptionID]);

    useEffect(() => {
      if (isNonEmptyVariable(originalChargeDescriptionID)) {
        api
          .get(
            `${API_ENDPOINTS.vwCharges_refListItemGlobalID}/${originalChargeDescriptionID}`,
          )
          .then(({ data }) => {
            if (data?.length) {
              applyChargeLevel(data[0].chargeLevel, "originalChargeLevelID");
              const ucr = data[0].refListItemAttribs?.find(
                (r) => r.attribName === "UOC_Code",
              )?.attribValue;
              if (isNonEmptyVariable(ucr))
                form.setFieldValue("originalUCRCode", ucr);
            }
          });
      } else {
        form.setFieldValue("originalChargeLevelID", "");
        form.setFieldValue("originalUCRCode", "");
      }
    }, [originalChargeDescriptionID]);

    // -----------------------------------------------------------------------
    // Scroll to relevant section on checkbox toggles
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (isNonEmptyVariable(sentenced) && sentenced) {
        form?.scrollToField(["sentence"], {
          behavior: "smooth",
          block: "center",
        });
        form.focusField(["sentence"]);
      }
    }, [sentenced]);

    useEffect(() => {
      if (isNonEmptyVariable(requiresBond) && requiresBond) {
        form?.scrollToField(["chooseBailType"], {
          behavior: "smooth",
          block: "center",
        });
        form.focusField(["chooseBailType"]);
      }
    }, [requiresBond]);

    useEffect(() => {
      if (clearedToGo && !fullSearch) {
        form.setFieldValue("clearedBy", loginUser?.staffId);
        form.setFieldValue("clearanceDateTime", dayjs());
        form?.scrollToField(["clearedBy"], {
          behavior: "smooth",
          block: "center",
        });
        form.focusField(["clearedBy"]);
      } else {
        form.setFieldValue("clearedBy", null);
        form.setFieldValue("clearanceDateTime", null);
      }
    }, [clearedToGo]);

    // -----------------------------------------------------------------------
    // Original custody reason → deadline fields
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (
        originalCustodyReasonID === 54180001 ||
        originalCustodyReasonID === 54180002
      ) {
        form.setFieldsValue({
          deadlineStartDateTime: formData?.deadlineStartDateTime || null,
          deadlineDateTime1: formData?.deadlineDateTime1,
          deadlineDateTime2: formData?.deadlineDateTime2,
          deadlineSatisfied1: formData?.deadlineSatisfied1,
          deadlineSatisfiedDateTime1: formData?.deadlineSatisfiedDateTime1,
          deadlineSatisfied2: formData?.deadlineSatisfied2,
          deadlineSatisfiedDateTime2: formData?.deadlineSatisfiedDateTime2,
        });
      } else {
        form.setFieldsValue({
          deadlineStartDateTime: null,
          deadlineSatisfied1: false,
          deadlineSatisfiedDateTime1: null,
          deadlineSatisfied2: false,
          deadlineSatisfiedDateTime2: null,
          deadlineDateTime1Local: null,
          deadlineDateTime2Local: null,
        });
      }
    }, [originalCustodyReasonID]);

    // -----------------------------------------------------------------------
    // Disposition date auto-fill
    // -----------------------------------------------------------------------
    useEffect(() => {
      form.setFieldValue(
        "dispositionDate",
        isNonEmptyVariable(offenderDispositionID) ? dayjs() : null,
      );
    }, [offenderDispositionID]);

    // -----------------------------------------------------------------------
    // Deadline date calculation
    // -----------------------------------------------------------------------
    const generateAppearanceDate = (appearanceDate, holidayList) => {
      const dateStr = new Date(appearanceDate?.$d).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      if (holidayList?.length) {
        const isHoliday = holidayList.find((e) => e.holidayDate === dateStr);
        return isHoliday
          ? generateAppearanceDate(
              dayjs(appearanceDate).add(24, "hour").hour(0).minute(0),
              holidayList,
            )
          : appearanceDate;
      }
      return appearanceDate;
    };

    useEffect(() => {
      if (isNonEmptyVariable(deadlineStartDateTime)) {
        const appearanceDate = generateAppearanceDate(
          dayjs(deadlineStartDateTime).add(36, "hour").hour(0).minute(0),
          holidays,
        );
        const pcDate = dayjs(deadlineStartDateTime).add(48, "hour");
        form.setFieldsValue({
          deadlineDateTime1Local: appearanceDate,
          deadlineDateTime2Local: pcDate,
          deadlineSatisfied1: formData?.deadlineSatisfied1,
          deadlineSatisfiedDateTime1:
            formData?.deadlineSatisfiedDateTime1 || null,
          deadlineSatisfied2: formData?.deadlineSatisfied2,
          deadlineSatisfiedDateTime2:
            formData?.deadlineSatisfiedDateTime2 || null,
        });
      } else {
        form.setFieldsValue({
          deadlineDateTime1Local: null,
          deadlineDateTime2Local: null,
          deadlineSatisfied1: false,
          deadlineSatisfied2: false,
          deadlineSatisfiedDateTime1: null,
          deadlineSatisfiedDateTime2: null,
        });
      }
    }, [deadlineStartDateTime, holidays]);

    // -----------------------------------------------------------------------
    // Sentence Consecutive To → pull Sentence Start Date/Time from the
    // referenced charge's Scheduled Release Date/Time, skipping a day when
    // that charge's own sentence was long enough to have gotten the
    // 1-minute credit (Sentence_24hrDay_Limit). Mirrors the consecutive-
    // charge chain in ChargesInterface.asp/ChargeReleaseDateCalculator.vbs.
    //
    // Resolves against `chargesData` — the already-saved charges for this
    // booking — not a live in-memory graph of unsaved edits, so the charge
    // being chained to must be saved first. This is a scope-limited stand-in
    // for legacy's full topological/cycle-checked resolution, which isn't
    // practical to replicate across a one-charge-at-a-time edit form.
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (!sentenceConsecutive || !isNonEmptyVariable(sentenceConsecutiveTo)) {
        return;
      }

      const targetSequence = parseInt(sentenceConsecutiveTo);
      if (isNaN(targetSequence)) return;

      const target = (chargesData || []).find(
        (c) =>
          c?.sequence === targetSequence &&
          (!isNonEmptyVariable(formData?.uuid) || c?.uuid !== formData?.uuid),
      );

      if (!target || !isNonEmptyVariable(target?.scheduledReleaseDate)) {
        return;
      }

      let start = dayjs(
        `${target.scheduledReleaseDate} ${target.scheduledReleaseTime || "00:00"}`,
      );
      const targetSentenceLength = isNonEmptyVariable(target?.sentenceLength)
        ? parseInt(target.sentenceLength)
        : 0;
      if (targetSentenceLength > sentence24hrDayLimit) {
        start = start.add(1, "day");
      }

      form.setFieldValue("sentenceStartDateTime", start);
    }, [
      sentenceConsecutive,
      sentenceConsecutiveTo,
      chargesData,
      formData?.uuid,
      sentence24hrDayLimit,
    ]);

    // -----------------------------------------------------------------------
    // EHM? checked → default EHM Days from the client param when blank.
    // Mirrors EHMChanged() in ChargesInterface.asp.
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (useEHM && ehm && !isNonEmptyVariable(ehmDays)) {
        form.setFieldValue("ehmDays", parseInt(numberEHMDaysDefault) || 0);
      }
      if (!ehm) {
        form.setFieldValue("ehmDays", null);
      }
    }, [ehm]);

    // -----------------------------------------------------------------------
    // Sentence start date + length & adjustments → good time days, total
    // days, End Date, Scheduled Release Date/Time.
    //
    // Mirrors ChargeReleaseDateCalculator.vbs (CalculateReleaseDateForCharge):
    //   baseNumberOfDays = sentenceLength - stayed - treatment - EHM days
    //     (EHM days only counted when EHM? is checked)
    //   creditForOneMinute = 0 when sentenceLength <= 5, else 1
    //   goodTimeDays = floor(baseNumberOfDays / 3) once sentenceLength > 9
    //                  and baseNumberOfDays >= 10, else 0
    //   totalDays = baseNumberOfDays - creditDaysServed - goodTimeDays
    //               + goodTimeDaysLost
    //   End Date = start + (sentenceLength - creditForOneMinute) days
    //   Scheduled Release Date = start + (totalDays - creditForOneMinute) days
    //   Scheduled Release Time = start's own time when sentenceLength <= 5,
    //                            else the default release time (04:00)
    // A single effect (rather than splitting on start-date vs. adjustment
    // fields) so every dependency recalculates consistently regardless of
    // which field the user fills in last.
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (!isNonEmptyVariable(sentenceStartDateTime)) {
        // No start date, no computed dates — mirrors legacy clearing
        // Scheduled_Release_Date/Time when Sentence_Start_Date is blank.
        form.setFieldsValue({
          goodTimeDays: 0,
          totalDaysForThisCharge: 0,
          sentenceEndDate: null,
          scheduledReleaseDateTime: null,
        });
        return;
      }

      const sl = isNonEmptyVariable(sentenceLength)
        ? parseInt(sentenceLength)
        : 0;
      const ssd = isNonEmptyVariable(stayedOrSuspendedDays)
        ? parseInt(stayedOrSuspendedDays)
        : 0;
      const td = isNonEmptyVariable(treatmentDays)
        ? parseInt(treatmentDays)
        : 0;
      const cd = isNonEmptyVariable(creditDaysServed)
        ? parseInt(creditDaysServed)
        : 0;
      // Legacy only counts EHM days when the charge's own EHM? checkbox is
      // checked (independent of whether the Bookings\UseEHM section is
      // shown) — an unchecked/blank EHM Days always contributes 0.
      const ehmD = ehm && isNonEmptyVariable(ehmDays) ? parseInt(ehmDays) : 0;

      const baseNumberOfDays = sl - ssd - td - ehmD;
      const creditForOneMinute = sl > 5 ? 1 : 0;

      const gtd =
        sl > 9
          ? goodTimeDaysRule === "Base10Rule"
            ? Math.floor(baseNumberOfDays / 10)
            : baseNumberOfDays >= 10
              ? Math.floor(baseNumberOfDays / 3)
              : 0
          : 0;

      // Good Time Days Lost only factors into the total once sentenceLength
      // > 9 — legacy zeroes it out (and hides the field) for the <=5 and
      // 6-9 brackets, since neither one awards good time days to lose.
      const gtdl =
        sl > 9 && isNonEmptyVariable(goodTimeDaysLost)
          ? parseInt(goodTimeDaysLost)
          : 0;
      if (sl <= 9) {
        form.setFieldValue("goodTimeDaysLost", 0);
      }

      // Additional Good Time Days works opposite of Lost Good Time Days
      // (it shortens the sentence) and, per legacy, only applies once
      // sentenceLength > 9 — shorter sentences always treat it as 0.
      const agtd =
        useAdditionalGoodTimeDays &&
        sl > 9 &&
        isNonEmptyVariable(additionalGoodTimeDays)
          ? parseInt(additionalGoodTimeDays)
          : 0;
      if (useAdditionalGoodTimeDays && sl <= 9) {
        form.setFieldValue("additionalGoodTimeDays", 0);
      }

      const totalDays = baseNumberOfDays - cd - gtd + gtdl - agtd;

      const endDate = sentenceStartDateTime.add(
        sl - creditForOneMinute,
        "day",
      );
      const releaseDate = sentenceStartDateTime.add(
        totalDays - creditForOneMinute,
        "day",
      );
      const releaseDateTime =
        sl <= 5
          ? releaseDate
              .hour(sentenceStartDateTime.hour())
              .minute(sentenceStartDateTime.minute())
          : releaseDate.hour(defaultReleaseHour).minute(defaultReleaseMinute);

      form.setFieldValue("goodTimeDays", gtd);
      form.setFieldValue("totalDaysForThisCharge", totalDays);
      form.setFieldValue("sentenceEndDate", endDate);
      form.setFieldValue("scheduledReleaseDateTime", releaseDateTime);
    }, [
      sentenceStartDateTime,
      sentenceLength,
      stayedOrSuspendedDays,
      treatmentDays,
      goodTimeDaysLost,
      creditDaysServed,
      ehm,
      ehmDays,
      additionalGoodTimeDays,
      goodTimeDaysRule,
      defaultReleaseHour,
      defaultReleaseMinute,
    ]);

    // -----------------------------------------------------------------------
    // Clearance reason change warning
    // -----------------------------------------------------------------------
    const changeClearReason = (e) => {
      if (
        isNonEmptyVariable(formData?.clearanceReasonID) &&
        formData.clearanceReasonID !== e
      ) {
        showDialog("Warning", {
          title: "Reminder",
          content:
            "You must also update the Release Reason in the Release Information section of the Arrest Report",
          confirmButtonText: "Ok",
        });
      }
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <FieldSecurityProvider
        entity="Charges"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <AddChargesContainer>
          <Form
            form={form}
            scrollToFirstError={{
              behavior: "instant",
              block: "end",
              focus: true,
            }}
            onBlur={(e) => {
              if (
                e.target.id === "sentenceLength" &&
                parseInt(sentenceLength) <= 5 &&
                !isNonEmptyVariable(sentenceStartDateTime)
              ) {
                showDialog("info", {
                  title: "Warning",
                  content:
                    "Sentence start date is required when sentence length is 5 days or less",
                  confirmButtonText: "Ok",
                });
              }
            }}
          >
            <Flex gap="middle">
              {/* Controlling Charge? */}
              <SecureFormItem
                fieldName="controllingCharge"
                formItemProps={{
                  name: "controllingCharge",
                  valuePropName: "checked",
                  justify: "space-between",
                  rules: !fullSearch
                    ? controllingChargeValidation
                      ? [
                          {
                            validator: (_, value) =>
                              value
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error("Controlling Charge is required"),
                                  ),
                          },
                        ]
                      : null
                    : [],
                }}
              >
                <Checkbox
                  className="ant-text-dark"
                  variant={INPUT_VARIANT.INTAKE_FORM}
                  labelStyle={{ whiteSpace: "nowrap" }}
                >
                  Controlling Charge?
                </Checkbox>
              </SecureFormItem>

              {/* Requires Bail? */}
              <SecureFormItem
                fieldName="requiresBond"
                formItemProps={{
                  name: "requiresBond",
                  valuePropName: "checked",
                  justify: "space-between",
                }}
              >
                <Checkbox
                  className="ant-text-dark"
                  variant={INPUT_VARIANT.INTAKE_FORM}
                  labelStyle={{ whiteSpace: "nowrap" }}
                >
                  Requires Bail?
                </Checkbox>
              </SecureFormItem>

              {/* Sentenced? */}
              <SecureFormItem
                fieldName="sentenced"
                formItemProps={{
                  name: "sentenced",
                  valuePropName: "checked",
                  justify: "space-between",
                }}
              >
                <Checkbox
                  className="ant-text-dark"
                  variant={INPUT_VARIANT.INTAKE_FORM}
                  labelStyle={{ whiteSpace: "nowrap" }}
                >
                  Sentenced?
                </Checkbox>
              </SecureFormItem>

              {/* Cleared To Go? */}
              <SecureFormItem
                fieldName="clearedToGo"
                formItemProps={{
                  name: "clearedToGo",
                  valuePropName: "checked",
                  justify: "space-between",
                }}
              >
                <Checkbox
                  className="ant-text-dark"
                  variant={INPUT_VARIANT.INTAKE_FORM}
                  labelStyle={{ whiteSpace: "nowrap" }}
                >
                  Cleared To GO?
                </Checkbox>
              </SecureFormItem>

              {/* Sealed? */}
              <SecureFormItem
                fieldName="sealed"
                formItemProps={{
                  name: "sealed",
                  valuePropName: "checked",
                  justify: "space-between",
                }}
              >
                <Checkbox
                  className="ant-text-dark"
                  variant={INPUT_VARIANT.INTAKE_FORM}
                  labelStyle={{ whiteSpace: "nowrap" }}
                >
                  Sealed?
                </Checkbox>
              </SecureFormItem>
            </Flex>

            <ChargesSectionContainer>
              <ChargeInformation
                originalCustodyReasonID={originalCustodyReasonID}
                startingPoint={startingPoint}
              />
              {clearedToGo && (
                <ClearanceInformation
                  changeClearReason={changeClearReason}
                  startingPoint={startingPoint}
                />
              )}
              {[54180001, 54180002].includes(originalCustodyReasonID) && (
                <DeadlineInformation
                  deadlineStartDateTime={deadlineStartDateTime}
                  startingPoint={startingPoint}
                />
              )}
              <OffenseInformation startingPoint={startingPoint} />
              <CourtInformation
                courtListsData={courtListsData}
                updateCourtListForm={updateCourtListForm}
                startingPoint={startingPoint}
              />
              {sentenced && (
                <SentencingInformation
                  sentenceConsecutive={sentenceConsecutive}
                  releaseDateOverridden={releaseDateOverridden}
                  sentenceLength={sentenceLength}
                  useEHM={useEHM}
                  ehm={ehm}
                  useAdditionalGoodTimeDays={useAdditionalGoodTimeDays}
                  startingPoint={startingPoint}
                />
              )}
              {requiresBond && (
                <BailInformation
                  form={form}
                  isEdit={isEdit}
                  bookingBails={bookingBails}
                  formData={formData}
                  startingPoint={startingPoint}
                />
              )}
              <OriginalChargeInformation
                originalChargeDescriptionID={originalChargeDescriptionID}
                startingPoint={startingPoint}
                form={form}
                chargesExtraFields={chargesExtraFields}
              />
              {/* <AdministrativeInformation /> */}
            </ChargesSectionContainer>

            {isBooking && <FormActionsFooter />}
          </Form>
        </AddChargesContainer>
      </FieldSecurityProvider>
    );
  },
);

export default AddCharges;
